import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/markdoc/tags/index.ts'
import * as nodes from '/vercel/path0/markdoc/nodes/index.ts'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer();

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: \"Crypto Adoption doesn't have to be so hard – two case studies for positioning your product\"\ndate: 2020-05-25T00:25:15-08:00\ncategories: [\"product\"]\ndescription: \"We present a repeatable process to apply to your product roadmap to achieve product-market fit and wide adoption.\"\ndraft: true\ntype: legacy\n---\n\nI came across the questionnaire by IDEO. The main differentiator for IDEO is they get deeply involved in the projects they back. They not only do the UI/UX design but also get involved in the economic/incentive design on the protocol front. \n\n\nA question that stands out from the survey is:\n\n\n![Image](https://www.covalenthq.com/static/images/blog/202005/adoption-survey.png)\n \n\n \n\n\nThe question above is a great insight into how they think about the crypto space. It addresses the fundamental challenge of the product managers concerning:\n\n* Market sentiments - the general attitude of developers in the market\n* Consumer pain-points - specific problem areas developers are facing\n* Needs and aspirations - unsatisfied desires that motivate their actions\n\nThe entire crypto industry is working diligently towards mass-market adoption. How do crypto product managers know that they are directionally correct when the state of the world is still a vision and not a current reality. Vision or Hallucination?\n\n\n\n\nIn our framework, there are two primary ways of navigating in the right direction:\n\n* Make crypto adoption and on-ramps easier - grow existing user-base\n* Point crypto innovations towards the mainstream, existing user-bases\n\n\n## Numbers behind the crypto market\n\n|Options|Current size|CAGR ~ 3 years|\n|----|---|---|\n|Developers with non-crypto experience|~25 M|~1.5x|\n|Developers with Crypto experience|~50,000|~10x|\n\n\n## Case study 1: Near protocol\n\n\n![Image](https://www.covalenthq.com/static/images/blog/202005/adoption-near.png)\n \n\n \n\n\n\nWith NEAR, developers can build and deploy scalable decentralized applications in minutes on their developer-friendly blockchain. With the narrative that NEAR has, they are fundamentally making it easier for non-crypto developers to onboard on their blockchain.\n\nTheir thesis is that there are currently and optimistically about 10K dev in the crypto world. If they can grow the number of crypto devs to 500K (10x growth) – that’s a massive opportunity they are unlocking.\n\n\n## Case study 2: Fortmatic\n\n\n\n![Image](https://www.covalenthq.com/static/images/blog/202005/adoption-magic.png)\n \n\n \n\n\nFortmatic, with their flagship product Magic, eliminates the need for passwords by making them customizable, future proof, and passwordless. Developers can essentially build apps with a few lines of code.\n\nTheir approach is to target existing devs (25 million) and capture a meaningful share of the market. So, even if they were able to convert 1% of the existing user-base, they will have 250K devs, which is pretty exciting.\n\n\n## Conclusions\n\nBoth the case-studies tell us that you need to considerately focus on a specific use-case. Please read our previous blog post on narrow use-cases with a meme-able narrative.\n\n\nWhat other case-studies you think are focusing on peculiar use-cases? \n";
const filepath = "/blog/crypto-adoption-product-positioning.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
